import PhotoSwipe from 'photoswipe';
// eslint-disable-next-line import/no-unresolved
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin';

// Create a global object to hold the element's variables and config
const pswpLightboxCreator = {
  items: [],
  parents: new Set(),
  lightboxes: new Set(),
  parentIdAttribute: 'data-lightbox-parent-id',
  itemIdAttribute: 'data-lightbox-item-id',
};

/**
 * Set the lightbox caption's content for current image
 * @param {HTMLElement} el
 * @param {PhotoSwipeLightbox} lightbox
 */
function handleChange(el, lightbox) {
  const currSlideElement = lightbox.pswp.currSlide.data.element;
  if (!currSlideElement) return;

  el.innerHTML = '';
  el.setAttribute('hidden', '');

  setCaption(
    el,
    currSlideElement.getAttribute('title')
  );

  setCaptionLink(
    el,
    currSlideElement.getAttribute('data-lightbox-item-link'),
    currSlideElement.getAttribute('data-lightbox-item-link-label')
  );

  if (el.children.length > 0) el.removeAttribute('hidden');
}

/**
 * Replace the lightbox caption with captionText
 * @param {HTMLElement} el
 * @param {string} captionText
 */
function setCaption(el, captionText) {
  if (!el || !captionText) return;

  const captionParagraph = document.createElement('p');
  captionParagraph.textContent = captionText;
  el.append(captionParagraph);
}

/**
 * Add link to lightbox caption
 * @param {HTMLElement} el
 * @param {string} captionLinkHref
 * @param {string} captionLinkLabel
 */
function setCaptionLink(el, captionLinkHref, captionLinkLabel) {
  if (!el || !captionLinkHref || !captionLinkLabel) return;

  const captionLink = document.createElement('a');
  captionLink.href = captionLinkHref;
  captionLink.textContent = captionLinkLabel;
  captionLink.classList.add('button', 'button--secondary');
  el.append(captionLink);
}

/**
 * Set up the lightbox to show the required elements
 * @param {PhotoSwipeLightbox} lightbox
 */
function handleUiRegister(lightbox) {
  lightbox.pswp.ui.registerElement({
    name: 'caption',
    order: 9,
    isButton: false,
    appendTo: 'root',
    onInit: (el) => {
      lightbox.pswp.element.setAttribute('aria-label', 'Lightbox');
      lightbox.pswp.on('change', () => handleChange(el, lightbox));
    },
  });
}

/**
 * Initialize  single instance of a PhotoSwipe lightbox
 * @param {HTMLElement} parent
 */
function initLightbox(parent) {
  const allItems = Array.from(parent.querySelectorAll(`[${pswpLightboxCreator.itemIdAttribute}]`));

  const lightbox = new PhotoSwipeLightbox({
    gallery: parent,
    children: allItems,
    pswpModule: PhotoSwipe,
    allowPanToNext: true,
    preloaderDelay: 0,
    initialZoomLevel: 'fit',
    showHideAnimationType: 'fade',
  });

  lightbox.on('uiRegister', () => handleUiRegister(lightbox));

  lightbox.videoPlugin = new PhotoSwipeVideoPlugin(lightbox, {
    autoplay: false,
  });

  lightbox.init();

  pswpLightboxCreator.lightboxes.add(lightbox);

  parent.dispatchEvent(new CustomEvent('toujou-lightbox-init', {
    bubbles: true,
    composed: true,
    detail: {
      el: parent,
      lightbox,
    },
  }));
}

/**
 * Initialize a lightbox element for each of the lightbox parent element
 */
function initParents() {
  pswpLightboxCreator.parents.forEach((parent) => {
    initLightbox(parent);
  });
}

/**
 * Initialize the element by getting all lightbox items from the page
 */
function initPhotoswipeLightboxCreator() {
  pswpLightboxCreator.items = document.querySelectorAll(`[${pswpLightboxCreator.itemIdAttribute}]`);

  pswpLightboxCreator.items.forEach((item) => {
    const itemLightboxParent = item.closest(`[${pswpLightboxCreator.parentIdAttribute}]`);
    pswpLightboxCreator.parents.add(itemLightboxParent);
  });

  initParents();
}

/**
 * Wait for the page to completely load before initializing the photoswipe-lightbox-creator script
 */
if (document.readyState !== 'loading') {
  setTimeout(() => {
    initPhotoswipeLightboxCreator();
  });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    initPhotoswipeLightboxCreator();
  });
}
